import { Marked, marked } from 'marked';

// Configure marked for GitHub Flavored Markdown
marked.setOptions({
  breaks: true,
  gfm: true,
});

/**
 * Basic HTML sanitization — same approach used throughout the project
 * (see src/app/history/fieldFormatters/html.ts)
 * Content comes from our own backend, not user input.
 */
export function sanitizeHtml(html: string): string {
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '')
    .replace(/javascript:/gi, '');
}

/**
 * Strip sandbox file links (e.g. [Download](sandbox:/mnt/data/file.png))
 * that the code interpreter model generates — these are not accessible in the browser.
 * Images are delivered via the file_output SSE event and rendered inline in code blocks.
 */
function stripSandboxLinks(md: string): string {
  // Convert [text](sandbox:/path/to/file) → text
  return md.replace(/\[([^\]]*)\]\(sandbox:[^)]*\)/g, '$1');
}

/**
 * Render markdown string to sanitized HTML
 */
export function renderMarkdown(markdown: string): string {
  if (!markdown) {
    return '';
  }
  const cleaned = stripSandboxLinks(markdown);
  const html = marked.parse(cleaned, { async: false }) as string;

  return sanitizeHtml(html);
}

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  colon: ':',
  tab: '\t',
  newline: '\n',
  nbsp: ' ',
  sol: '/',
  period: '.',
  lpar: '(',
  rpar: ')',
};

function decodeEntitiesOnce(value: string): string {
  return value.replace(
    /&(#x[0-9a-f]+|#[0-9]+|[a-z]+);?/gi,
    (match, entity: string) => {
      if (entity[0] === '#') {
        const isHex = entity[1].toLowerCase() === 'x';
        const code = Number.parseInt(
          entity.slice(isHex ? 2 : 1),
          isHex ? 16 : 10,
        );

        return Number.isFinite(code) && code >= 0 && code <= 0x10ffff
          ? String.fromCodePoint(code)
          : '';
      }

      return NAMED_ENTITIES[entity.toLowerCase()] ?? match;
    },
  );
}

function decodeEntities(value: string): string {
  let current = value;
  for (let i = 0; i < 5; i++) {
    const next = decodeEntitiesOnce(current);
    if (next === current) {
      break;
    }
    current = next;
  }

  return current;
}

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g;
const SAFE_SCHEMES = new Set(['http:', 'https:', 'mailto:']);

type SafeLink = { href: string; external: boolean };

/**
 * Allowlist check for a model-supplied URL. Decides on the entity-decoded,
 * whitespace/control-stripped, lower-cased form, so obfuscations such as
 * `java&#x73;cript:` or `java\tscript:` cannot slip through.
 */
export function toSafeLink(
  rawHref: string | null | undefined,
): SafeLink | null {
  if (!rawHref) {
    return null;
  }
  const decoded = decodeEntities(rawHref).replace(CONTROL_CHARS, '');
  const normalized = decoded.replace(/\s/g, '').toLowerCase();
  if (!normalized) {
    return null;
  }

  const scheme = /^([a-z][a-z0-9+.-]*):/.exec(normalized)?.[1];
  if (scheme) {
    if (!SAFE_SCHEMES.has(`${scheme}:`)) {
      return null;
    }

    return { href: decoded.trim(), external: scheme !== 'mailto' };
  }
  if (normalized.startsWith('\\') || normalized.startsWith('/\\')) {
    return null;
  }

  return { href: decoded.trim(), external: normalized.startsWith('//') };
}

const untrustedMarked = new Marked({
  breaks: true,
  gfm: true,
  renderer: {
    html({ text }) {
      return escapeHtml(text);
    },
    link({ href, title, tokens }) {
      const label = this.parser.parseInline(tokens);
      const safe = toSafeLink(href);
      if (!safe) {
        return label;
      }
      const titleAttr = title ? ` title="${escapeHtml(title)}"` : '';
      const externalAttrs = safe.external
        ? ' target="_blank" rel="noopener noreferrer"'
        : '';

      return `<a href="${escapeHtml(safe.href)}"${titleAttr}${externalAttrs}>${label}</a>`;
    },
    image({ text }) {
      return escapeHtml(text);
    },
  },
});

/**
 * Render markdown that comes from an untrusted source (LLM output). Raw HTML is
 * escaped, links are restricted to http(s)/mailto/relative URLs and images are
 * reduced to their alt text so nothing loads from a remote host.
 */
export function renderUntrustedMarkdown(markdown: string): string {
  if (!markdown) {
    return '';
  }

  return untrustedMarked.parse(stripSandboxLinks(markdown), {
    async: false,
  }) as string;
}

// --- AI entity references --------------------------------------------------
// The AI assistant embeds clickable entity references using the token format
//   {{entity:<type>:<id>|<label>}}
//   {{entity:<type>:<id>:parent=<activityId>|<label>}}   (action / completion_criterion)
// Backend prompt: workflows/src/services/ai/entity-references.ts
// Click handler: src/composables/useEntityChipNavigation.ts

const ENTITY_TOKEN_RE =
  /\{\{entity:(activity|milestone|phase|workstream|action|completion_criterion):([A-Za-z0-9_-]+)(?::parent=([A-Za-z0-9_-]+))?\|((?:[^}]|\}(?!\}))+)\}\}/g;

// Inline SVG icons for each chip type. The first four mirror the
// `BeeXxxOutline` icons used by the @-mention dropdown (their source is
// `src/ui/icons/custom/<type>-outline.svg`) — duplicated inline here rather
// than imported via `?raw` because `vite-svg-loader` intercepts SVG imports
// and the raw-query bypass isn't reliable in this setup. Action and
// completion_criterion use Lucide outlines. All six use the same 24-unit
// viewBox + `stroke="currentColor"` convention so they share styling and
// inherit the chip's text color.
const ENTITY_ICONS: Record<string, string> = {
  // activity / milestone / phase / workstream: exact paths from
  // `src/ui/icons/custom/<type>-outline.svg` so they visually match the
  // @-mention dropdown's icons.
  activity: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12.0622L15.5 18.1244L8.5 18.1244L5 12.0622L8.5 6L15.5 6L19 12.0622Z"/></svg>`,
  milestone: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="12" y="5" width="10" height="10" transform="rotate(45 12 5)"/></svg>`,
  phase: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7H17L20 12L17 17H4L7 12L4 7Z"/></svg>`,
  workstream: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="7" width="14" height="10"/></svg>`,
  // action / completion_criterion aren't @-mentionable so they have no Bee
  // outline; using Lucide Zap and CheckSquare in the same outline style.
  action: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>`,
  completion_criterion: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>`,
};

/**
 * Escape the five HTML-significant characters. Exported so anything that builds
 * markup by hand (citation snippets, entity chips) uses one implementation —
 * an escaper that drifts is an XSS hole waiting for its second copy.
 */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Replace `{{entity:...}}` tokens in the markdown source with `<button>`
 * elements carrying data attributes. Runs BEFORE markdown parsing so the
 * surrounding markdown (bold, lists, etc.) still applies to the chip.
 * The marked library passes raw HTML through, and `sanitizeHtml` leaves
 * `<button data-...>` alone (no scripts, no on* handlers).
 */
function replaceEntityTokens(markdown: string): string {
  return markdown.replace(
    ENTITY_TOKEN_RE,
    (
      _match,
      type: string,
      id: string,
      parentId: string | undefined,
      label: string,
    ) => {
      const safeLabel = escapeHtml(label.trim());
      const safeType = escapeHtml(type);
      const safeId = escapeHtml(id);
      const parentAttr = parentId
        ? ` data-entity-parent-id="${escapeHtml(parentId)}"`
        : '';
      const iconSvg = ENTITY_ICONS[type] ?? '';
      const iconSpan = iconSvg
        ? `<span class="bb-entity-chip__icon" aria-hidden="true">${iconSvg}</span>`
        : '';

      return `<a role="link" tabindex="0" class="bb-entity-chip" data-entity-type="${safeType}" data-entity-id="${safeId}"${parentAttr}>${iconSpan}<span class="bb-entity-chip__label">${safeLabel}</span></a>`;
    },
  );
}

/**
 * Render markdown for AI assistant messages. Same pipeline as renderMarkdown
 * but with entity-reference token replacement applied first, so the model can
 * embed clickable links to project/template entities.
 */
export function renderAiMessageMarkdown(markdown: string): string {
  if (!markdown) {
    return '';
  }
  const withChips = replaceEntityTokens(markdown);
  const cleaned = stripSandboxLinks(withChips);
  const html = marked.parse(cleaned, { async: false }) as string;

  return sanitizeHtml(html);
}
